import { useEffect, useMemo, useState } from "react";
import {
  LIST_SEARCH_DEBOUNCE_MS,
  readQ,
  setOrDelete,
} from "@/modules/shell/list-url-filters";
import { useListUrlParams } from "@/modules/shell/use-list-url-params";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { SortingState } from "@tanstack/react-table";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProductGlancePeek } from "@/modules/inventory/components/ProductGlancePeek";
import { listProducts, type ProductListSort } from "@/shared/api/inventory";
import { queryKeys } from "@/shared/query/keys";
import {
  AdminFilters,
  adminFilterControlClass,
} from "@/modules/shell/AdminFilters";
import { DataTable } from "@/modules/shell/data-table";
import {
  DataTableChrome,
  DeskListFilterReset,
} from "@/modules/shell/DataTableChrome";
import { DETAIL_COMPACT_MQ } from "@/modules/shell/detail-edit-mode";
import { useDeskListFilterReset } from "@/modules/shell/use-desk-list-filter-reset";
import {
  ListSearchStatus,
  listSearchOnlinePending,
} from "@/modules/shell/ListSearchStatus";
import { DeskListHeader } from "@/modules/shell/DeskListHeader";
import { DeskSubNav } from "@/modules/shell/DeskSubNav";
import { stockModuleTabs } from "@/modules/shell/desk-module-tabs";
import { EmptyState } from "@/modules/shell/EmptyState";
import { PageFrame } from "@/modules/shell/PageFrame";
import { productColumns } from "./products-columns";

const PAGE_SIZE = 50;
const SORTABLE: ProductListSort[] = ["name", "sku", "kind", "avgCost"];

function sortingToApi(sorting: SortingState): {
  sort: ProductListSort;
  sortDir: "asc" | "desc";
} {
  const first = sorting[0];
  if (first && SORTABLE.includes(first.id as ProductListSort)) {
    return {
      sort: first.id as ProductListSort,
      sortDir: first.desc ? "desc" : "asc",
    };
  }
  return { sort: "name", sortDir: "asc" };
}

export function ProductsPage() {
  const { companySlug } = useParams();
  const navigate = useNavigate();
  const { searchParams, patchParams } = useListUrlParams();
  const [query, setQuery] = useState(() => readQ(searchParams));
  const [debouncedQ, setDebouncedQ] = useState(() => readQ(searchParams));
  const [sorting, setSorting] = useState<SortingState>([]);
  const [glanceId, setGlanceId] = useState<string | null>(null);
  const [compact, setCompact] = useState(() =>
    typeof window !== "undefined"
      ? window.matchMedia(DETAIL_COMPACT_MQ).matches
      : true,
  );
  const { sort, sortDir } = sortingToApi(sorting);

  useEffect(() => {
    const mq = window.matchMedia(DETAIL_COMPACT_MQ);
    const onChange = () => setCompact(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    const t = window.setTimeout(() => {
      const q = query.trim();
      setDebouncedQ(q);
      patchParams((p) => setOrDelete(p, "q", q));
    }, LIST_SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query, patchParams]);

  const productsQuery = useInfiniteQuery({
    queryKey: queryKeys.products.list(companySlug ?? "", {
      includeDeactivated: true,
      q: debouncedQ,
      limit: PAGE_SIZE,
      sort,
      sortDir,
    }),
    enabled: Boolean(companySlug),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      listProducts(companySlug!, {
        includeDeactivated: true,
        q: debouncedQ || undefined,
        limit: PAGE_SIZE,
        cursor: pageParam,
        sort,
        sortDir,
        signal,
      }),
    getNextPageParam: (last) =>
      last.hasMore && last.nextCursor ? last.nextCursor : undefined,
  });

  const errorMsg =
    productsQuery.error instanceof Error
      ? productsQuery.error.message
      : productsQuery.isError
        ? "Failed to load"
        : null;

  useEffect(() => {
    if (errorMsg === "Not signed in") {
      navigate("/login", { replace: true });
    }
  }, [errorMsg, navigate]);

  const columns = useMemo(
    () =>
      companySlug
        ? productColumns({
            companySlug,
            // Phone → full detail; desk → glance peek (plan/40).
            onGlance: compact
              ? undefined
              : (id) => setGlanceId(id),
          })
        : [],
    [companySlug, compact],
  );

  const filtersActive = Boolean(query.trim());
  const filterReset = useDeskListFilterReset({
    patchParams,
    setQuery: setQuery,
    canResetToDefault: filtersActive,
    canClearAll: filtersActive,
  });
  const searchingOnline = listSearchOnlinePending({
    query,
    debouncedQ,
    isFetching: productsQuery.isFetching,
  });

  if (!companySlug) return null;

  const products =
    productsQuery.data?.pages.flatMap((p) => p.products) ?? [];
  const currency =
    productsQuery.data?.pages[0]?.company.currencyCode ?? "PHP";
  const total = productsQuery.data?.pages[0]?.total;
  const loading = productsQuery.isPending;
  const error = errorMsg && errorMsg !== "Not signed in" ? errorMsg : null;
  const q = debouncedQ;


  return (
    <PageFrame>
      <DataTableChrome>
      <DeskListFilterReset {...filterReset} />
      <DeskListHeader
        title="Products"
        description={`Masters for POS & SO · prices in ${currency}`}
        tabs={
          <DeskSubNav
            placement="header"
            items={stockModuleTabs(companySlug)}
            aria-label="Stock views"
          />
        }
        primaryAction={
          <Button asChild>
            <Link
              to={`/c/${companySlug}/products/new`}
              className="no-underline"
            >
              New product
            </Link>
          </Button>
        }
      />

      <AdminFilters>
        <Input
          className={`${adminFilterControlClass} min-w-[12rem] flex-1`}
          placeholder="Search name, SKU, barcode…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          disabled={loading && products.length === 0}
        />
      </AdminFilters>
      <ListSearchStatus online searching={searchingOnline} />
      {!loading && products.length > 0 && total != null ? (
        <p className="mt-2 text-sm text-[var(--color-muted)]">
          Showing {products.length}
          {total > products.length ? ` of ${total}` : ""}
        </p>
      ) : null}

      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}
      {loading && products.length === 0 && (
        <Alert variant="muted" className="mt-8">
          Loading products…
        </Alert>
      )}

      {!loading && products.length === 0 && !error ? (
        <div className="mt-8">
          <EmptyState
            title={q ? `No match for “${q}”` : "No products yet"}
            description={
              q
                ? "Try clearing search or another name, SKU, or barcode."
                : "Create one to sell from POS or add to a sales order."
            }
            action={
              q ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => filterReset.onClearAll()}
                >
                  Clear filters
                </Button>
              ) : (
                <Button asChild size="sm">
                  <Link
                    to={`/c/${companySlug}/products/new`}
                    className="no-underline"
                  >
                    New product
                  </Link>
                </Button>
              )
            }
          />
        </div>
      ) : null}

      {products.length > 0 ? (
        <>
          <DataTable
            onRefresh={() => void productsQuery.refetch()}
            listId="products"
            companySlug={companySlug}
            columns={columns}
            data={products}
            getRowId={(r) => r.id}
            sorting={sorting}
            onSortingChange={setSorting}
            defaultVisibility={{ barcode: false, avgCost: false }}
          />
          {productsQuery.hasNextPage ? (
            <div className="mt-4 flex justify-center">
              <Button
                type="button"
                variant="outline"
                disabled={productsQuery.isFetchingNextPage}
                pending={productsQuery.isFetchingNextPage}
                pendingLabel="Loading more…"
                onClick={() => void productsQuery.fetchNextPage()}
              >
                Load more
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
          </DataTableChrome>

      <ProductGlancePeek
        open={Boolean(glanceId) && !compact}
        productId={glanceId}
        companySlug={companySlug}
        listPath={`/c/${companySlug}/products`}
        onOpenChange={(open) => {
          if (!open) setGlanceId(null);
        }}
      />
    </PageFrame>
  );
}
